import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "./config";
import "./css/PortalImport.css";

const fmtBytes = (b) => {
  if (!b || isNaN(b)) return "";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  return `${(b / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

// Browse Ensembl + Ensembl Genomes (plants/fungi/protists/metazoa/bacteria) live
// and download selected files. Everything is fetched from the portal on demand.
const EnsemblImportPage = () => {
  const navigate = useNavigate();
  const [divisions, setDivisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [division, setDivision] = useState("vertebrates");
  const [divisionMeta, setDivisionMeta] = useState({}); // key -> {huge}

  const [speciesQuery, setSpeciesQuery] = useState("");
  const [species, setSpecies] = useState([]);
  const [release, setRelease] = useState("");
  const [needsQuery, setNeedsQuery] = useState(false);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [picked, setPicked] = useState(null); // {species, display_name, assembly}

  const [category, setCategory] = useState("dna");
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileFilter, setFileFilter] = useState("");
  const [selected, setSelected] = useState({});

  const [projectName, setProjectName] = useState("");
  const [launching, setLaunching] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/public-data/ensembl/divisions`, { withCredentials: true });
        setDivisions(res.data.divisions || []);
        setCategories(res.data.categories || []);
        const meta = {}; (res.data.divisions || []).forEach((d) => { meta[d.key] = d; });
        setDivisionMeta(meta);
      } catch (_) { setMessage({ type: "error", text: "Could not load Ensembl divisions." }); }
    })();
  }, []);

  const huge = !!(divisionMeta[division] && divisionMeta[division].huge);

  // Fetch species when division or query changes (debounced).
  useEffect(() => {
    let cancel = false;
    const q = speciesQuery.trim();
    const run = async () => {
      setLoadingSpecies(true); setNeedsQuery(false);
      try {
        const res = await axios.get(`${API_BASE}/api/public-data/ensembl/species`,
          { params: { division, q }, withCredentials: true });
        if (cancel) return;
        setRelease(res.data.release || "");
        setNeedsQuery(!!res.data.needsQuery);
        setSpecies(res.data.species || []);
      } catch (_) { if (!cancel) setMessage({ type: "error", text: "Could not reach Ensembl. Try again." }); }
      finally { if (!cancel) setLoadingSpecies(false); }
    };
    const t = setTimeout(run, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [division, speciesQuery]);

  const changeDivision = (d) => {
    setDivision(d); setPicked(null); setSpecies([]); setSpeciesQuery(""); setFiles([]); setSelected({}); setMessage(null);
  };

  const loadFiles = async (sp, cat) => {
    setLoadingFiles(true); setFiles([]); setSelected({});
    try {
      const res = await axios.get(`${API_BASE}/api/public-data/ensembl/files`,
        { params: { division, species: sp, category: cat }, withCredentials: true });
      setFiles(res.data.files || []);
    } catch (_) { setMessage({ type: "error", text: "Could not list files for this selection." }); }
    finally { setLoadingFiles(false); }
  };

  const pickSpecies = (s) => { setPicked(s); setMessage(null); loadFiles(s.species, category); };
  const changeCategory = (c) => { setCategory(c); if (picked) loadFiles(picked.species, c); };

  const shownFiles = fileFilter.trim()
    ? files.filter((f) => f.name.toLowerCase().includes(fileFilter.trim().toLowerCase()))
    : files;
  const selectedUrls = Object.keys(selected).filter((u) => selected[u]);

  const download = async () => {
    if (!projectName.trim()) { setMessage({ type: "error", text: "Enter a project name." }); return; }
    if (!/^[A-Za-z0-9_]+$/.test(projectName)) { setMessage({ type: "error", text: "Project name may only contain letters, numbers, and underscores." }); return; }
    if (!selectedUrls.length) { setMessage({ type: "error", text: "Select at least one file." }); return; }
    if (selectedUrls.length > 50) { setMessage({ type: "error", text: "Maximum 50 files per request." }); return; }
    setLaunching(true); setMessage(null);
    try {
      const res = await axios.post(`${API_BASE}/api/public-data/import`,
        { source: "ensembl", projectName: projectName.trim(), ids: selectedUrls }, { withCredentials: true });
      setMessage({ type: "success", text: `Download started for ${res.data.summary?.okCount || selectedUrls.length} file(s). Redirecting to your downloads…` });
      setSelected({});
      setTimeout(() => navigate("/downloads", { state: { message: "Your download request has been submitted." } }), 900);
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.error || "Failed to start download." });
    } finally { setLaunching(false); }
  };

  return (
    <div className="portal-import">
      <h1>Download Ensembl Data</h1>
      <p className="portal-intro">Browse Ensembl and Ensembl Genomes (plants, fungi, protists, metazoa, bacteria) live and download any files to your workspace.</p>

      {/* Step 1: division + species */}
      <div className="portal-step">
        <div className="portal-step-title"><span className="portal-step-num">1</span>Choose a division & organism</div>
        <div className="portal-controls">
          <select className="portal-select" value={division} onChange={(e) => changeDivision(e.target.value)}>
            {divisions.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <input className="portal-search"
            placeholder={huge ? "Search species (required — e.g. escherichia, coli)" : "Search species (e.g. human, homo, arabidopsis)"}
            value={speciesQuery} onChange={(e) => setSpeciesQuery(e.target.value)} />
          {loadingSpecies && <span className="portal-spinner" />}
        </div>
        {release && <div className="portal-count">Release {release}{huge ? " · large division — type to search" : ""}</div>}
        {picked ? (
          <div className="portal-muted">Selected: <strong>{picked.display_name}</strong> <span className="portal-row-sub">({picked.species}{picked.assembly ? `, ${picked.assembly}` : ""})</span> ·{" "}
            <button className="portal-linkbtn" onClick={() => { setPicked(null); setFiles([]); setSelected({}); }}>change</button>
          </div>
        ) : needsQuery ? (
          <div className="portal-file portal-muted">Type at least 2 characters to search this division.</div>
        ) : (
          <div className="portal-list">
            {species.map((s) => (
              <div key={s.species} className="portal-row" onClick={() => pickSpecies(s)}>
                <span>{s.display_name}<div className="portal-row-sub">{s.species}{s.assembly ? ` · ${s.assembly}` : ""}{s.assembly_accession ? ` · ${s.assembly_accession}` : ""}</div></span>
              </div>
            ))}
            {!species.length && !loadingSpecies && <div className="portal-file portal-muted">No matching species.</div>}
          </div>
        )}
      </div>

      {/* Step 2: category + files */}
      {picked && (
        <div className="portal-step">
          <div className="portal-step-title"><span className="portal-step-num">2</span>Choose a data type & files</div>
          <div className="portal-controls">
            <select className="portal-select" value={category} onChange={(e) => changeCategory(e.target.value)}>
              {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <input className="portal-search" placeholder="Filter files by name" value={fileFilter} onChange={(e) => setFileFilter(e.target.value)} />
            {loadingFiles && <span className="portal-spinner" />}
          </div>
          <div className="portal-count">{selectedUrls.length} selected · {shownFiles.length} file(s) shown</div>
          <div className="portal-list">
            {shownFiles.map((f) => (
              <div className="portal-file" key={f.url}>
                <label>
                  <input type="checkbox" checked={!!selected[f.url]} onChange={() => setSelected((p) => ({ ...p, [f.url]: !p[f.url] }))} />
                  {f.name}
                </label>
                <span className="portal-file-size">{fmtBytes(f.size)}</span>
              </div>
            ))}
            {!shownFiles.length && !loadingFiles && <div className="portal-file portal-muted">No files here.</div>}
          </div>
        </div>
      )}

      {picked && (
        <div className="portal-actions">
          <input className="portal-project" placeholder="Project name (a-z, 0-9, _)" value={projectName} onChange={(e) => setProjectName(e.target.value)} />
          <button className="portal-btn" disabled={launching || !selectedUrls.length} onClick={download}>
            {launching ? "Starting…" : `Download ${selectedUrls.length || ""} file(s)`}
          </button>
          {message && <span className={message.type === "error" ? "portal-error" : "portal-success"}>{message.text}</span>}
        </div>
      )}
    </div>
  );
};

export default EnsemblImportPage;
